/**
 * « Changer de commerce » sur le PDA (super admin) : même impersonation officielle que le
 * portail d'administration (tracée), on reste dans le cadre VisionStock.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { TenantsApi } from '@/api/tenants';
import { UsersApi } from '@/api/users';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useImpersonate } from '@/contexts/ImpersonateContext';

type TenantChoice = { id: string; name: string; adminId: string | null; adminName: string };

const loadTenantChoices = async (): Promise<TenantChoice[]> => {
  const [tenants, users] = await Promise.all([TenantsApi.listTenants(), UsersApi.adminListUsers()]);
  if (!tenants.ok) throw new Error('Commerces non chargés');
  const admins = new Map<string, { id: string; name: string }>();
  for (const user of users.ok ? users.value : []) {
    if (user.role === 'admin' && user.tenant_id && !admins.has(user.tenant_id)) {
      admins.set(user.tenant_id, { id: user.id, name: user.display_name || user.email });
    }
  }
  return tenants.value
    .map((tenant) => ({
      id: tenant.id,
      name: tenant.name,
      adminId: admins.get(tenant.id)?.id ?? null,
      adminName: admins.get(tenant.id)?.name ?? 'Admin',
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
};

export function TenantSwitchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { startImpersonate } = useImpersonate();
  const [search, setSearch] = useState('');
  const {
    data = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['pda_tenant_choices'],
    enabled: open,
    staleTime: 60_000,
    queryFn: loadTenantChoices,
  });
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter((tenant) => tenant.name.toLowerCase().includes(q)) : data;
  }, [data, search]);

  const choose = (tenant: TenantChoice) => {
    if (!tenant.adminId) {
      toast.error('Aucun admin dans ce commerce');
      return;
    }
    startImpersonate(tenant.adminId, tenant.adminName, tenant.name);
    onClose();
    navigate('/mode-stock?vue=maj');
    toast.success(`Commerce : ${tenant.name}`);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-md" data-testid="tenant-switch">
        <DialogHeader>
          <DialogTitle>Changer de commerce</DialogTitle>
          <DialogDescription>Le PDA reste en VisionStock.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Nom du commerce"
            aria-label="Rechercher un commerce"
            className="h-12 pl-10 text-base"
          />
        </div>
        {isLoading && <Loader2 className="mx-auto h-6 w-6 animate-spin" />}
        {error && <p className="text-base text-destructive">{(error as Error).message}</p>}
        <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto rounded-lg border border-border">
          {shown.map((tenant) => (
            <li key={tenant.id}>
              <button
                type="button"
                className="flex min-h-14 w-full items-center px-3 text-left text-base active:bg-muted disabled:opacity-50"
                disabled={!tenant.adminId}
                onClick={() => choose(tenant)}
              >
                {tenant.name}
              </button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
